import { AlertTriangle, Loader2 } from "lucide-react";
import { Modal } from "./Modal";
import { Button } from "./Button";

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  /** Optional second paragraph for context the user should see before confirming. */
  detail?: string;
  /** Label on the destructive/confirm button. Defaults to "Confirm". */
  confirmLabel?: string;
  /** Label on the cancel button. Defaults to "Cancel". */
  cancelLabel?: string;
  /** Visual tone of the confirm button — destructive uses red. Defaults to destructive. */
  tone?: "destructive" | "neutral";
  /** When true, the confirm button shows a spinner and is disabled. */
  isLoading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Drop-in replacement for `window.confirm` that matches the app's dark theme
 * and animation style. Use this for any irreversible action — deleting a tag,
 * a digital twin, removing an asset, etc.
 */
export function ConfirmDialog({
  isOpen,
  title,
  message,
  detail,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  tone = "destructive",
  isLoading = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        if (!isLoading) onCancel();
      }}
      title={title}
      size="sm"
    >
      <div className="space-y-4">
        <div className="flex items-start gap-3">
          <div
            className={
              "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg " +
              (tone === "destructive"
                ? "bg-red-500/15 text-red-400"
                : "bg-amber-500/15 text-amber-400")
            }
          >
            <AlertTriangle className="h-4 w-4" />
          </div>
          <div className="space-y-2">
            <p className="text-sm text-surface-200">{message}</p>
            {detail && <p className="text-xs text-surface-400">{detail}</p>}
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onCancel} disabled={isLoading}>
            {cancelLabel}
          </Button>
          <Button
            variant={tone === "destructive" ? "danger" : "default"}
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
            {confirmLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
